import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { useConfigurations } from '@/features/_data/useConfigurations';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import { Modal } from '@/ui/Modal/Modal';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';
import { fetcher } from '@/utils/fetcher';
import { useCreateUnit, useUnitData } from '@/features/_data/useUnitData';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { useIntl } from 'react-intl';
import { useReadScripts } from '@/features/_data/useScripts';
import { useLogicData } from '@/features/_data/useLogicData';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  unitId: string | undefined;
}

const StyledSaveButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  align-self: flex-end;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}
`;

export const DuplicateUnitModal = ({
  isModalOpen,
  setIsModalOpen,
  unitId,
}: Props) => {
  const [selectedCustomerId] = useSelectedCustomerId();
  const [currentUnitName, setCurrentUnitName] = useState<string>('');

  const { refetch: refetchConfigurations } =
    useConfigurations(selectedCustomerId);

  const toast = useToast();
  const intl = useIntl();
  const { data: originalUnitData, isLoading: isOriginalDataLoading } =
    useUnitData(unitId);
  const { data: originalUnitScript, isLoading: isOriginalScriptLoading } =
    useReadScripts(unitId ?? '');
  const {
    data: logicData,
    isError: isErrorLoadingLogicData,
    isLoading: isLogicDataLoading,
  } = useLogicData(unitId ?? '');

  const isLoading =
    isOriginalDataLoading || isOriginalScriptLoading || isLogicDataLoading;

  useEffect(() => {
    if (isModalOpen && originalUnitData?.name) {
      setCurrentUnitName(`${originalUnitData?.name} (Copy)`);
    }
  }, [isModalOpen, originalUnitData?.name]);

  const { createUnit } = useCreateUnit();

  const handleSave = async () => {
    if (
      !originalUnitData?.customer ||
      !currentUnitName ||
      !originalUnitScript
    ) {
      return;
    }

    if (!navigator.onLine) {
      toast({
        mode: TOASTTYPE.No_Connection,
        subtitle: intl.formatMessage({
          defaultMessage: 'Please connect to the internet to save this change.',
          id: 'dbYfjx',
        }),
      });
      return;
    }

    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: originalId,
      members,
      readAccess,
      slideGroups,
      slides,
      nfcTagIdList,
      structure,
      writeAccess,
      homeTimer,
      languageSupport,
      selectedMappingTag,
      slideDeleteDisallowed,
      ...dataToDuplicate
    } = originalUnitData;

    const result = await createUnit({
      ...dataToDuplicate,
      name: currentUnitName,
      customer: originalUnitData?.customer,
      complexityType: 'complex',
      logicEditorEnabled: originalUnitData.logicEditorEnabled,
    });

    await fetcher(`${RETAIL_API_URL}/units/update`, {
      method: 'POST',
      body: JSON.stringify({
        ...result,
        members,
        nfcTagIdList,
        readAccess,
        slideGroups,
        slides,
        structure,
        writeAccess,
        homeTimer,
        languageSupport,
        selectedMappingTag,
        slideDeleteDisallowed,
        createdAt: new Date().toISOString(),
      }),
    });

    await fetcher(`${RETAIL_API_URL}/scripts/${result.id}`, {
      method: 'POST',
      body: JSON.stringify({ body: originalUnitScript.script }),
    });

    // When configs are initially set up there is are no blocks configured in logic-editor.
    // So the request responds with resource not found and the logic data does not have to be duplicated.
    if (!isErrorLoadingLogicData) {
      await fetcher(
        `${RETAIL_API_URL}/logicEditor/blocks?unitId=${result.id}`,
        {
          method: 'POST',
          body: JSON.stringify({
            nodes: logicData?.nodes,
            edges: logicData?.edges,
            settings: logicData?.settings,
            selectedSensors: logicData?.selectedSensors,
          }),
        },
      );
    }

    toast({
      title: intl.formatMessage({
        defaultMessage: 'Success! 🎉',
        id: 'JUR35N',
      }),
      subtitle: intl.formatMessage({
        defaultMessage: 'Unit duplicated!',
        id: 'FxIrN7',
      }),
      mode: TOASTTYPE.Success,
    });

    setIsModalOpen(false);
    refetchConfigurations();
  };

  return isModalOpen && originalUnitData?.name ? (
    <Modal
      onClose={() => setIsModalOpen(false)}
      title="Duplicate Configuration"
    >
      <Flex gap={16} direction="column">
        <Input
          testId={`name-duplicate-${unitId}`}
          onChange={(value) => setCurrentUnitName(value)}
          placeholder="Name"
          value={currentUnitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSave();
            }
          }}
        />
        <StyledSaveButton
          testId={`save-duplicate-${unitId}`}
          disabled={
            !currentUnitName.trim() ||
            !originalUnitData?.customer ||
            !originalUnitScript ||
            isLoading
          }
          onClick={handleSave}
        >
          {isLoading ? 'Fetching data...' : 'Save'}
        </StyledSaveButton>
      </Flex>
    </Modal>
  ) : null;
};
